// DV-5184 — dev-only functional interceptor that fakes the group-events API.
import {
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse
} from '@angular/common/http'
import { Observable, of } from 'rxjs'
import { delay, map } from 'rxjs/operators'

import {
    AttendeeSort,
    CreateGroupEventParam,
    CreateGroupEventSetupPayload,
    GroupEventInsightsResponse,
    GroupEventType
} from '@@interfaces/group-event.interface'
import {
    DUPLICATE_GROUP_EVENT_RESPONSE,
    ELIGIBLE_PACKAGES,
    GROUP_EVENTS,
    GROUP_EVENT_IDS,
    GROUP_EVENT_INSIGHTS,
    GROUP_EVENT_POLICIES,
    GROUP_EVENT_TYPE_COUNTS,
    PAY_RATES,
    SCHEDULES,
    SESSIONS_SUMMARY,
    buildCreatedGroupEvent,
    findGroupEvent
} from './group-events.fixtures'

export const GROUP_EVENTS_MOCK_DELAY_MS = 300

const API_MARKER = 'api/v1/'
const MOCKED_PREFIXES = ['api/v1/group-events', 'api/v1/trainers']
// Trailing slashes keep `getBookingEvents/` from matching `getBookingEventsSummary/`.
const LEGACY_EVENTS_PATH = 'bookingAPI/getBookingEvents/'
const LEGACY_DETAILS_PATH = 'bookingAPI/getBookingEventDetails/'

// Minimal typing of the legacy bookingAPI envelopes we decorate; everything else is passed through.
interface LegacyEventRow {
    [key: string]: unknown
    id?: number
    groupEventId?: number
    isHidden?: boolean
    isCancelled?: boolean
}

interface LegacyEventsBody {
    [key: string]: unknown
    events?: LegacyEventRow[]
}

interface LegacyDetailsBody {
    [key: string]: unknown
    entity?: LegacyEventRow
}

interface ParsedUrl {
    segments: string[]
    query: URLSearchParams
}

const ok = (body: unknown): Observable<HttpEvent<unknown>> =>
    of(new HttpResponse({ status: 200, body })).pipe(
        delay(GROUP_EVENTS_MOCK_DELAY_MS)
    )

const noContent = (): Observable<HttpEvent<unknown>> =>
    of(new HttpResponse({ status: 204, body: null })).pipe(
        delay(GROUP_EVENTS_MOCK_DELAY_MS)
    )

const splitUrl = (url: string): ParsedUrl => {
    const qIndex = url.indexOf('?')
    const path = qIndex === -1 ? url : url.slice(0, qIndex)
    const query = new URLSearchParams(
        qIndex === -1 ? '' : url.slice(qIndex + 1)
    )
    const start = path.indexOf(API_MARKER)
    const rest = start === -1 ? '' : path.slice(start + API_MARKER.length)
    return { segments: rest.split('/').filter((s) => s.length > 0), query }
}

// ':' segments are wildcards.
const matches = (segments: string[], pattern: string[]): boolean =>
    segments.length === pattern.length &&
    pattern.every((p, i) => p === ':' || p === segments[i])

const toId = (segment: string): number => Number.parseInt(segment, 10)

const sortedAttendees = (sort: string | null): GroupEventInsightsResponse => {
    if (sort !== AttendeeSort.NAME) {
        return GROUP_EVENT_INSIGHTS
    }
    const rows = [...(GROUP_EVENT_INSIGHTS.attendeeRows ?? [])].sort((a, b) =>
        (a.clientName ?? '').localeCompare(b.clientName ?? '')
    )
    return { ...GROUP_EVENT_INSIGHTS, attendeeRows: rows }
}

// Returns null for an api/v1 path the mock does not know so the real backend can answer it.
const resolveGroupEventsApi = (
    req: HttpRequest<unknown>
): Observable<HttpEvent<unknown>> | null => {
    const { segments, query } = splitUrl(req.urlWithParams)
    const method = req.method.toUpperCase()

    // ---- trainers ----
    if (matches(segments, ['trainers', ':', 'pay-rates']) && method === 'GET') {
        return ok(PAY_RATES)
    }

    // ---- collection ----
    if (matches(segments, ['group-events'])) {
        if (method === 'GET') {
            const type = query.get('type')
            const list = type
                ? GROUP_EVENTS.filter(
                      (g) => g.type === (type as GroupEventType)
                  )
                : GROUP_EVENTS
            return ok(list)
        }
        if (method === 'POST') {
            return ok(buildCreatedGroupEvent(req.body as CreateGroupEventParam))
        }
        return null
    }
    if (matches(segments, ['group-events', 'types']) && method === 'GET') {
        return ok(GROUP_EVENT_TYPE_COUNTS)
    }
    if (
        matches(segments, ['group-events', 'with-setup']) &&
        method === 'POST'
    ) {
        const payload = req.body as CreateGroupEventSetupPayload
        return ok(buildCreatedGroupEvent(payload.groupEvent))
    }

    // ---- sessions (PROPOSED) ----
    if (
        matches(segments, ['group-events', 'sessions', 'summary']) &&
        method === 'GET'
    ) {
        return ok(SESSIONS_SUMMARY)
    }
    if (
        matches(segments, ['group-events', 'sessions', ':']) &&
        method === 'PUT'
    ) {
        return noContent()
    }
    if (
        matches(segments, ['group-events', 'sessions', ':', 'uncancel']) &&
        method === 'POST'
    ) {
        return noContent()
    }
    if (
        (matches(segments, ['group-events', 'sessions', ':', 'visibility']) ||
            matches(segments, [
                'group-events',
                'sessions',
                ':',
                'substitute'
            ])) &&
        method === 'PUT'
    ) {
        return noContent()
    }

    // ---- single group event ----
    if (matches(segments, ['group-events', ':'])) {
        if (method === 'GET') {
            return ok(findGroupEvent(toId(segments[1])))
        }
        if (method === 'PUT' || method === 'DELETE') {
            return noContent()
        }
        return null
    }
    if (
        matches(segments, ['group-events', ':', 'duplicate']) &&
        method === 'POST'
    ) {
        return ok(DUPLICATE_GROUP_EVENT_RESPONSE)
    }

    // ---- schedules ----
    if (matches(segments, ['group-events', ':', 'schedules'])) {
        if (method === 'GET') {
            const includeArchived = query.get('includeArchived') === 'true'
            return ok(
                includeArchived
                    ? SCHEDULES
                    : SCHEDULES.filter((s) => !s.isArchived)
            )
        }
        if (method === 'POST') {
            return noContent()
        }
        return null
    }
    if (
        matches(segments, ['group-events', ':', 'schedules', ':']) &&
        (method === 'PUT' || method === 'DELETE')
    ) {
        return noContent()
    }
    if (
        matches(segments, [
            'group-events',
            ':',
            'schedules',
            ':',
            'end-date'
        ]) &&
        method === 'PUT'
    ) {
        return noContent()
    }

    // ---- packages / policies ----
    if (matches(segments, ['group-events', ':', 'packages'])) {
        if (method === 'GET') {
            return ok(ELIGIBLE_PACKAGES)
        }
        if (method === 'PUT') {
            return noContent()
        }
        return null
    }
    if (matches(segments, ['group-events', ':', 'policies'])) {
        if (method === 'GET') {
            return ok(GROUP_EVENT_POLICIES)
        }
        if (method === 'PUT') {
            return noContent()
        }
        return null
    }

    // ---- insights / attendees ----
    if (
        matches(segments, ['group-events', ':', 'insights']) &&
        method === 'GET'
    ) {
        return ok(GROUP_EVENT_INSIGHTS)
    }
    if (
        matches(segments, ['group-events', ':', 'attendees']) &&
        method === 'GET'
    ) {
        return ok(sortedAttendees(query.get('sort')))
    }

    return null
}

// Legacy week list: rows gain groupEventId (cycling fixture ids by position), isHidden and isCancelled
// defaults. Existing values are never overwritten.
const decorateBookingEvents = (
    event: HttpEvent<unknown>
): HttpEvent<unknown> => {
    if (!(event instanceof HttpResponse)) {
        return event
    }
    const body = event.body as LegacyEventsBody | null
    if (!body || !Array.isArray(body.events)) {
        return event
    }
    const events = body.events.map((row, i) => ({
        ...row,
        groupEventId:
            row.groupEventId ?? GROUP_EVENT_IDS[i % GROUP_EVENT_IDS.length],
        isHidden: row.isHidden ?? false,
        isCancelled: row.isCancelled ?? false
    }))
    return event.clone({ body: { ...body, events } })
}

// Legacy session details: entity gains groupEventId (keyed by session id so it is stable per session).
const decorateBookingEventDetails = (
    event: HttpEvent<unknown>
): HttpEvent<unknown> => {
    if (!(event instanceof HttpResponse)) {
        return event
    }
    const body = event.body as LegacyDetailsBody | null
    if (!body || !body.entity || body.entity.groupEventId != null) {
        return event
    }
    const sessionId = body.entity.id ?? 0
    const entity = {
        ...body.entity,
        groupEventId: GROUP_EVENT_IDS[sessionId % GROUP_EVENT_IDS.length]
    }
    return event.clone({ body: { ...body, entity } })
}

/**
 * DV-5184 — mock for the group-events API while the backend (DV-5215) lands.
 *
 * Registered ONLY when `environment.mockGroupEvents` is true; app.module.ts appends it to
 * `withInterceptors([...])` after the loader interceptor. It is never part of a production build.
 *
 * Requests whose URL contains `api/v1/group-events` or `api/v1/trainers` are answered from
 * `group-events.fixtures.ts` after a 300 ms delay: GETs return fixtures, POST/PUT/DELETE return
 * 204 (or the created detail for `with-setup` / `{groupEventId}` for `duplicate`). Unknown
 * `api/v1` paths fall through to the network so real 404s still exercise the degraded UI.
 *
 * Legacy `POST bookingAPI/getBookingEvents/` responses are decorated with `groupEventId`,
 * `isHidden` and `isCancelled`; `GET bookingAPI/getBookingEventDetails/` gains `groupEventId`.
 * Everything else passes through untouched.
 */
export const groupEventsMockInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    if (MOCKED_PREFIXES.some((prefix) => req.url.includes(prefix))) {
        const mocked = resolveGroupEventsApi(req)
        if (mocked) {
            return mocked
        }
        return next(req)
    }
    if (req.method === 'POST' && req.url.includes(LEGACY_EVENTS_PATH)) {
        return next(req).pipe(map(decorateBookingEvents))
    }
    if (req.method === 'GET' && req.url.includes(LEGACY_DETAILS_PATH)) {
        return next(req).pipe(map(decorateBookingEventDetails))
    }
    return next(req)
}
